/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See LICENSE in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

export type DecisionStatus = 'allow' | 'deny' | 'review';
export type ChainStatus = 'valid' | 'broken';

export interface TrustAgent {
  id: string;
  label: string;
  operatorRef: string;
  mandateRef: string;
  trustLevel: string;
  status: 'active' | 'frozen' | 'review';
  allowedActions: string[];
  spendLimit: string;
  velocityLimit: string;
  risk: 'low' | 'medium' | 'high';
}

export interface TrustDecision {
  id: string;
  agentId: string;
  action: string;
  resource: string;
  policy: string;
  status: DecisionStatus;
  reason: string;
  auditHash: string;
  timestamp: string;
  risk: 'low' | 'medium' | 'high';
}

export interface PolicyPack {
  name: string;
  description: string;
  allowedOperations: string[];
  velocityLimit: string;
  amountLimit: string;
  allowlist: string[];
}

export interface AuditEntry {
  seq: number;
  eventType: string;
  actor: string;
  decision: DecisionStatus;
  eventHash: string;
  previousHash: string;
}

export interface IntegrationTarget {
  name: string;
  type: string;
  status: 'configured' | 'planned' | 'reference';
  description: string;
}

export const agents: TrustAgent[] = [
  {
    id: 'agent-payops-001',
    label: 'PayOps reconciliation agent',
    operatorRef: 'operator:bank-ops-17',
    mandateRef: 'mandate:ops-reconcile-q2',
    trustLevel: 'L3 - governed',
    status: 'active',
    allowedActions: ['reconcile_invoice', 'read_balance', 'request_refund'],
    spendLimit: 'EUR 100 / action',
    velocityLimit: '40 actions / hour',
    risk: 'low',
  },
  {
    id: 'agent-risk-042',
    label: 'Fintech risk-review agent',
    operatorRef: 'operator:risk-team-04',
    mandateRef: 'mandate:risk-review-standard',
    trustLevel: 'L2 - limited',
    status: 'review',
    allowedActions: ['read_case', 'flag_case', 'request_human_approval'],
    spendLimit: 'No settlement authority',
    velocityLimit: '120 checks / hour',
    risk: 'medium',
  },
  {
    id: 'agent-commerce-118',
    label: 'E-commerce checkout agent',
    operatorRef: 'operator:merchant-demo',
    mandateRef: 'mandate:checkout-testnet',
    trustLevel: 'L3 - governed',
    status: 'active',
    allowedActions: ['create_checkout', 'verify_order', 'request_refund'],
    spendLimit: 'EUR 250 / cart',
    velocityLimit: '80 actions / hour',
    risk: 'low',
  },
  {
    id: 'agent-gameops-009',
    label: 'Game economy grant agent',
    operatorRef: 'operator:gameops-lead',
    mandateRef: 'mandate:currency-grants',
    trustLevel: 'L1 - sandbox',
    status: 'frozen',
    allowedActions: ['grant_currency'],
    spendLimit: 'EUR 20 equivalent',
    velocityLimit: '10 grants / hour',
    risk: 'high',
  },
];

export const decisions: TrustDecision[] = [
  {
    id: 'dec-1042',
    agentId: 'agent-payops-001',
    action: 'reconcile_invoice',
    resource: 'invoice:INV-2048',
    policy: 'Bank governance pilot',
    status: 'allow',
    reason: 'Operation, mandate, resource, amount, and velocity gates passed.',
    auditHash: '8d1f2a7c0b8f4c2aa0e7f4d9f9c121a2',
    timestamp: '2026-05-26T20:41:32Z',
    risk: 'low',
  },
  {
    id: 'dec-1041',
    agentId: 'agent-gameops-009',
    action: 'grant_currency',
    resource: 'player:high-risk-region',
    policy: 'Gaming economy controls',
    status: 'deny',
    reason: 'Agent frozen; jurisdiction/resource allowlist gate failed.',
    auditHash: 'e72a19fd57f0cc83258a9d9f0f92da44',
    timestamp: '2026-05-26T20:38:04Z',
    risk: 'high',
  },
  {
    id: 'dec-1040',
    agentId: 'agent-risk-042',
    action: 'flag_case',
    resource: 'case:AML-7791',
    policy: 'Fintech review workflow',
    status: 'review',
    reason: 'Allowed operation but high-risk evidence requires human review.',
    auditHash: '0f4e73bdac800bd381edc1a9b7a33128',
    timestamp: '2026-05-26T20:31:16Z',
    risk: 'medium',
  },
  {
    id: 'dec-1039',
    agentId: 'agent-commerce-118',
    action: 'create_checkout',
    resource: 'order:ORD-9031',
    policy: 'E-commerce checkout pilot',
    status: 'allow',
    reason: 'Checkout amount and merchant resource are inside policy.',
    auditHash: 'b655e9d2ebd86d2aa241ff84183da838',
    timestamp: '2026-05-26T20:25:55Z',
    risk: 'low',
  },
];

export const policyPacks: PolicyPack[] = [
  {
    name: 'Bank governance pilot',
    description: 'Reference policy for advisory decisions before the bank executes the action on its own systems.',
    allowedOperations: ['reconcile_invoice', 'read_balance', 'request_refund'],
    velocityLimit: '40 actions / hour',
    amountLimit: 'EUR 100 advisory ceiling',
    allowlist: ['invoice:*', 'account-ref:*', 'refund-request:*'],
  },
  {
    name: 'Fintech review workflow',
    description: 'Controls case review and escalation without replacing AML, KYC, or sanctions vendors.',
    allowedOperations: ['read_case', 'flag_case', 'request_human_approval'],
    velocityLimit: '120 checks / hour',
    amountLimit: 'No settlement authority',
    allowlist: ['case:*', 'customer-ref:*'],
  },
  {
    name: 'Partner/OEM embedded console',
    description: 'Embeddable proof widgets and tenant-level policy packs for partner dashboards.',
    allowedOperations: ['authorize_action', 'export_evidence', 'verify_chain'],
    velocityLimit: 'Tenant configured',
    amountLimit: 'Tenant configured',
    allowlist: ['tenant:*', 'evidence:*'],
  },
];

export const auditEntries: AuditEntry[] = [
  {
    seq: 1042,
    eventType: 'AUTHORIZE_DECISION',
    actor: 'agent-payops-001',
    decision: 'allow',
    eventHash: '8d1f2a7c0b8f4c2aa0e7f4d9f9c121a2',
    previousHash: 'e72a19fd57f0cc83258a9d9f0f92da44',
  },
  {
    seq: 1041,
    eventType: 'AUTHORIZE_DECISION',
    actor: 'agent-gameops-009',
    decision: 'deny',
    eventHash: 'e72a19fd57f0cc83258a9d9f0f92da44',
    previousHash: '0f4e73bdac800bd381edc1a9b7a33128',
  },
  {
    seq: 1040,
    eventType: 'AUTHORIZE_DECISION',
    actor: 'agent-risk-042',
    decision: 'review',
    eventHash: '0f4e73bdac800bd381edc1a9b7a33128',
    previousHash: 'b655e9d2ebd86d2aa241ff84183da838',
  },
  {
    seq: 1039,
    eventType: 'AUTHORIZE_DECISION',
    actor: 'agent-commerce-118',
    decision: 'allow',
    eventHash: 'b655e9d2ebd86d2aa241ff84183da838',
    previousHash: 'GENESIS',
  },
];

export const integrations: IntegrationTarget[] = [
  {
    name: 'PayBot Core API',
    type: 'API keys',
    status: 'reference',
    description: 'Connects to /authorize, /authorize/chain, agents, and policy surfaces.',
  },
  {
    name: 'Webhook delivery',
    type: 'Events',
    status: 'planned',
    description: 'Signed allow, deny, review, freeze, and audit export events.',
  },
  {
    name: 'SIEM export',
    type: 'Evidence',
    status: 'planned',
    description: 'Pushes audit evidence into bank or fintech security tooling.',
  },
  {
    name: 'Bank/fintech adapter config',
    type: 'Adapters',
    status: 'reference',
    description: 'BYO AML, KYC, PSD2, fraud, and case-management systems stay customer-owned.',
  },
];

export function getChainStatus(tampered: boolean): ChainStatus {
  return tampered ? 'broken' : 'valid';
}

export function evaluatePolicy(operation: string, amount: number, resource: string): { status: DecisionStatus; reason: string } {
  const bankPolicy = policyPacks[0];
  if (!bankPolicy.allowedOperations.includes(operation)) {
    return { status: 'deny', reason: 'Operation is not in the allowed operation set.' };
  }
  if (amount > 100) {
    return { status: 'deny', reason: 'Amount exceeds the bank pilot advisory ceiling.' };
  }
  if (!resource.startsWith('invoice:') && !resource.startsWith('refund-request:')) {
    return { status: 'review', reason: 'Resource is outside the simple allowlist and needs human review.' };
  }
  return { status: 'allow', reason: 'All reference policy checks pass.' };
}
